import React, { Component } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import tw from "twin.macro";
import styled from "styled-components";
import connectComponent from "../../redux/connectComponent";
import { Error } from "components/errors";
import withWrap from "components/withWrap";
import {
  BoxForm,
  BoxHeadline,
  InputForm,
  WButton,
  Divider,
  AlternativeOption
} from "components/BoxForm";

const Description = styled.p`
  ${tw`text-gray-600 mb-4`}
`;

const SubmitButton = styled(WButton)`
  ${tw`mt-4`}
`;

const STEP_REQUEST = "request";
const STEP_VERIFY = "verify";

export class OtpAuth extends Component {
  static propTypes = {
    actions: PropTypes.object,
    database: PropTypes.object,
    onAuth: PropTypes.func,
    onIncomplete: PropTypes.func,
    registrationPath: PropTypes.string,
    referrer: PropTypes.string
  };

  state = {
    step: STEP_REQUEST,
    identifier: "",
    code: "",
    error: null,
    disabled: false,
    channel: null,
    destination: null,
    expiresIn: null
  };

  constructor(props) {
    super(props);
    this.identifierRef = React.createRef();
    this.codeRefs = Array.from({ length: 6 }, () => React.createRef());
  }

  handleRequestSubmit = (e) => {
    e.preventDefault();
    if (this.state.disabled) return;

    this.setState({ disabled: true, error: null });

    this.props.actions
      .requestOtp(this.state.identifier)
      .then((response) => {
        this.setState({
          step: STEP_VERIFY,
          disabled: false,
          channel: response.channel,
          destination: response.destination,
          expiresIn: response.expires_in
        });
        // Focus first code input
        setTimeout(() => this.codeRefs[0].current?.focus(), 100);
      })
      .catch((err) => {
        this.setState({
          disabled: false,
          error: err.message || "Failed to send code. Please try again."
        });
      });
  };

  handleVerifySubmit = (e) => {
    e.preventDefault();
    if (this.state.disabled) return;

    this.setState({ disabled: true, error: null });

    this.props.actions
      .verifyOtp(this.state.identifier, this.state.code)
      .then((response) => {
        if (response.success) {
          this.onSuccess(response);
        } else {
          this.setState({
            disabled: false,
            error: response.error || "Invalid code",
            code: ""
          });
          this.codeRefs[0].current?.focus();
        }
      })
      .catch((err) => {
        this.setState({
          disabled: false,
          error: err.message || "Verification failed. Please try again.",
          code: ""
        });
        this.codeRefs[0].current?.focus();
      });
  };

  onSuccess = () => {
    this.props.actions
      .refresh()
      .then((user) => {
        this.props.actions.optimizeEvent("login");
        this.setState({ disabled: false });
        // Use returned user directly to avoid stale props issue
        this.goToDestination(user);
      })
      .catch((err) => console.error("Error after OTP login", err));
  };

  // Check profile completion using the user object directly
  // This avoids stale props issues after refresh()
  isProfileComplete = (user) => {
    if (!user) return false;
    const hasName = user.name && user.name.trim().length >= 3;
    const hasBasicFields = user.street && user.phone && user.postal_code;
    const hasRealEmail =
      user.email && !user.email.includes("@placeholder.dwellsocial.com");
    return hasName && hasBasicFields && hasRealEmail;
  };

  goToDestination = (user) => {
    if (!this.isProfileComplete(user)) {
      // Pass user data to parent so it can forward to UpdateInfo
      this.props.onIncomplete(user);
    } else if (this.props.onAuth) {
      this.props.onAuth();
    }
  };

  handleCodeChange = (index, value) => {
    // Only allow digits
    const digit = value.replace(/\D/g, "").slice(-1);

    const newCode = this.state.code.split("");
    newCode[index] = digit;
    const code = newCode.join("");

    this.setState({ code });

    // Auto-advance to next input
    if (digit && index < 5) {
      this.codeRefs[index + 1].current?.focus();
    }

    // Auto-submit when complete
    if (code.length === 6 && !code.includes("")) {
      setTimeout(
        () => this.handleVerifySubmit({ preventDefault: () => {} }),
        100
      );
    }
  };

  handleCodeKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !this.state.code[index] && index > 0) {
      this.codeRefs[index - 1].current?.focus();
    }
  };

  handleCodePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (paste.length === 6) {
      this.setState({ code: paste }, () => {
        this.codeRefs[5].current?.focus();
        setTimeout(
          () => this.handleVerifySubmit({ preventDefault: () => {} }),
          100
        );
      });
    }
  };

  handleResend = () => {
    this.setState({ step: STEP_REQUEST, code: "", error: null });
  };

  registrationPath = () => this.props.registrationPath || "/registration";

  renderRequestStep() {
    return (
      <BoxForm onSubmit={this.handleRequestSubmit}>
        <BoxHeadline>Sign in with code</BoxHeadline>

        <Description>
          Enter your email or phone number and we'll send you a login code.
        </Description>

        <Error error={this.state.error} />

        <InputForm>
          <label htmlFor="identifier">Email or Phone</label>
          <input
            type="text"
            id="identifier"
            autoFocus
            ref={this.identifierRef}
            value={this.state.identifier}
            onChange={(e) => this.setState({ identifier: e.target.value })}
            placeholder="you@example.com or (312) 555-1234"
            required
          />
        </InputForm>

        <SubmitButton
          value={this.state.disabled ? "Sending..." : "Send Code"}
          type="submit"
          disabled={this.state.disabled}
          data-testid="otp-send-code"
        />

        <Divider />

        <AlternativeOption>
          <Link to="/session/password">Sign in with password</Link>
        </AlternativeOption>

        <AlternativeOption>
          Don't have an account?{" "}
          <Link
            to={{
              pathname: this.registrationPath(),
              state: { referrer: this.props.referrer }
            }}
          >
            Sign up
          </Link>
        </AlternativeOption>
      </BoxForm>
    );
  }

  renderVerifyStep() {
    const codeArray = this.state.code.padEnd(6, " ").split("");

    return (
      <BoxForm onSubmit={this.handleVerifySubmit}>
        <BoxHeadline>Enter your code</BoxHeadline>

        <Description>
          We sent a 6-digit code to <strong>{this.state.destination}</strong>{" "}
          via {this.state.channel === "email" ? "email" : "SMS"}.
        </Description>

        <Error error={this.state.error} />

        <div className="otp-code-inputs" style={styles.codeContainer}>
          {codeArray.map((digit, index) => (
            <input
              key={index}
              ref={this.codeRefs[index]}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit.trim()}
              onChange={(e) => this.handleCodeChange(index, e.target.value)}
              onKeyDown={(e) => this.handleCodeKeyDown(index, e)}
              onPaste={this.handleCodePaste}
              style={styles.codeInput}
              autoFocus={index === 0}
            />
          ))}
        </div>

        <SubmitButton
          value={this.state.disabled ? "Verifying..." : "Verify Code"}
          type="submit"
          disabled={this.state.disabled || this.state.code.length < 6}
        />

        <Divider />

        <AlternativeOption>
          Didn't receive a code?{" "}
          <button
            type="button"
            onClick={this.handleResend}
            style={styles.linkButton}
          >
            Send again
          </button>
        </AlternativeOption>

        <AlternativeOption>
          <button
            type="button"
            onClick={() => this.setState({ step: STEP_REQUEST, code: "" })}
            style={styles.linkButton}
          >
            Use a different email or phone
          </button>
        </AlternativeOption>
      </BoxForm>
    );
  }

  render() {
    return this.state.step === STEP_REQUEST
      ? this.renderRequestStep()
      : this.renderVerifyStep();
  }
}

const styles = {
  codeContainer: {
    display: "flex",
    gap: "8px",
    justifyContent: "center",
    margin: "24px 0"
  },
  codeInput: {
    width: "48px",
    height: "56px",
    fontSize: "24px",
    textAlign: "center",
    border: "2px solid #ddd",
    borderRadius: "8px",
    outline: "none"
  },
  linkButton: {
    background: "none",
    border: "none",
    color: "#007bff",
    cursor: "pointer",
    textDecoration: "underline",
    padding: 0,
    font: "inherit"
  }
};

export default withWrap(connectComponent(OtpAuth, ["authentication", "users"]));
